import PlatformMcpRegistration from './components/PlatformMcpRegistration';
import { useState, useEffect, useRef, createContext, useContext, useMemo, lazy, Suspense, Component } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import AppLoadingShell from "./components/AppLoadingShell";

// Subdomain detection utility
import { getAdminPortalUrl, getMainAppUrl, getSubdomain, isAdminPortalEnabled, isAdminPortalHost, isStagingSharedPortal, isOrgSubdomain, isWebsiteProAdminPath, isWebsiteProHost } from "./utils/subdomain";
import { recoverFromChunkLoadError } from "./utils/chunkLoadRecovery";
import { adminPortalReturnPath } from "./config/adminPortalAuth";
import OrganizationAdminRedirect from "./components/OrganizationAdminRedirect";
import { isLegacyOrganizationAdminPath, legacyPlatformAdminDestination } from "./config/adminPortalRoutes";

// Error Boundary — catches runtime crashes and shows a recovery UI
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught:", error, info);
    recoverFromChunkLoadError(error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc" }}>
          <div style={{ textAlign: "center", maxWidth: 400, padding: 32 }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, color: "#0f172a", marginBottom: 8 }}>Something went wrong</h2>
            <p style={{ fontSize: 14, color: "#64748b", marginBottom: 16 }}>{this.state.error?.message || "An unexpected error occurred."}</p>
            <button
              onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
              style={{ padding: "10px 24px", background: "#0f172a", color: "#fff", border: "none", borderRadius: 12, cursor: "pointer", fontSize: 14, fontWeight: 500 }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Loading fallback component
const PageLoader = () => (
  <AppLoadingShell />
);

// =====================================================
// LAZY-LOADED PAGES (Code Splitting)
// =====================================================

// Auth Pages - loaded quickly for initial access
const LandingPage = lazy(() => import("./pages/LandingPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const GoogleCallback = lazy(() => import("./pages/GoogleCallback"));

// Parent/User Pages
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Programs = lazy(() => import("./pages/Programs"));
const ProgramDetail = lazy(() => import("./pages/ProgramDetail"));
const Household = lazy(() => import("./pages/Household"));
const FamilyFormPage = lazy(() => import("./pages/FamilyFormPage"));
const PlayerProfile = lazy(() => import("./pages/PlayerProfile"));
const MemberProfile = lazy(() => import("./pages/MemberProfile"));
const Registrations = lazy(() => import("./pages/Registrations"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const RegistrationPage = lazy(() => import("./pages/RegistrationPage"));
const RegistrationConfirmation = lazy(() => import("./pages/RegistrationConfirmation"));
const PaymentMethods = lazy(() => import("./pages/PaymentMethods"));
const TryoutInvites = lazy(() => import("./pages/TryoutInvites"));
const Profile = lazy(() => import("./pages/Profile"));
const CreateOrganization = lazy(() => import("./pages/CreateOrganization"));
const OrganizationApplicationStatus = lazy(() => import("./pages/OrganizationApplicationStatus"));

// Public Pages
const PublicScoreSubmit = lazy(() => import("./pages/PublicScoreSubmit"));
const PublicRegistrationPage = lazy(() => import("./pages/PublicRegistrationPage"));
const DiscoverPrograms = lazy(() => import("./pages/DiscoverPrograms"));
const IndependentCoachSignup = lazy(() => import("./pages/IndependentCoachSignup"));
const BecomeCoach = lazy(() => import("./pages/BecomeCoach"));

// Org Portal Pages
const OrgPortal = lazy(() => import("./pages/OrgPortal"));
const OrgLogin = lazy(() => import("./pages/OrgLogin"));
const OrgRegister = lazy(() => import("./pages/OrgRegister"));
const PublicWebsitePro = lazy(() => import("./pages/PublicWebsitePro"));

// Coach Pages
const CoachRegistration = lazy(() => import("./pages/CoachRegistration"));
const CoachPending = lazy(() => import("./pages/CoachPending"));
const CoachDashboard = lazy(() => import("./pages/CoachDashboard"));

// Admin Pages - larger bundle, loaded only when needed
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminMarketplace = lazy(() => import("./pages/AdminMarketplace"));
const AdminWebsitePro = lazy(() => import("./pages/AdminWebsitePro"));
const AdminWstlControlCenter = lazy(() => import("./pages/AdminWstlControlCenter"));
const AdminDeveloperPlatform = lazy(() => import("./pages/AdminDeveloperPlatform"));
const AdminConnect = lazy(() => import("./pages/AdminConnect"));
const DeveloperOAuthConsent = lazy(() => import("./pages/DeveloperOAuthConsent"));
const MCPConnect = lazy(() => import("./pages/MCPConnect"));
const AdminTournamentModule = lazy(() => import("./pages/AdminTournamentModule"));
const AdminLeagueModule = lazy(() => import("./pages/AdminLeagueModule"));
const PublicTournamentApplication = lazy(() => import("./pages/PublicTournamentApplication"));
const PublicTournamentLive = lazy(() => import("./pages/PublicTournamentLive"));
const PublicLeagueSchedule = lazy(() => import("./pages/PublicLeagueSchedule"));
const AdminPrograms = lazy(() => import("./pages/AdminPrograms"));
const AdminProgramDashboard = lazy(() => import("./pages/AdminProgramDashboard"));
const AdminOrganizationSettings = lazy(() => import("./pages/AdminOrganizationSettings"));
const AdminHouseholds = lazy(() => import("./pages/AdminHouseholds"));
const AdminHouseholdDetail = lazy(() => import("./pages/AdminHouseholdDetail"));
const AdminFinancials = lazy(() => import("./pages/AdminFinancials"));
const AdminInvoices = lazy(() => import("./pages/AdminInvoices"));
const AdminMessaging = lazy(() => import("./pages/AdminMessaging"));
const AdminScheduling = lazy(() => import("./pages/AdminScheduling"));
const AdminPeople = lazy(() => import("./pages/AdminPeople"));
const AdminProfile = lazy(() => import("./pages/AdminProfile"));
const AdminFacilities = lazy(() => import("./pages/AdminFacilities"));
const AdminCoaches = lazy(() => import("./pages/AdminCoaches"));
// AdminMeetings functionality is now integrated into AdminScheduling
const AdminImport = lazy(() => import("./pages/AdminImport"));
const AdminSupport = lazy(() => import("./pages/AdminSupport"));
const AdminCompliance = lazy(() => import("./pages/AdminCompliance"));
const AdminReports = lazy(() => import("./pages/AdminReports"));
const AdminPaymentPlans = lazy(() => import("./pages/AdminPaymentPlans"));
const CoachCompliance = lazy(() => import("./pages/CoachCompliance"));
const Shop = lazy(() => import("./pages/Shop"));
const AdminPromoCodes = lazy(() => import("./pages/AdminPromoCodes"));
const ImportHub = lazy(() => import("./pages/ImportHub"));
const AdminSocial = lazy(() => import("./pages/AdminSocial"));
const ProgramTypeSelector = lazy(() => import("./pages/ProgramTypeSelector"));
const CreateProgramPage = lazy(() => import("./pages/CreateProgramPage"));
const EditProgramPage = lazy(() => import("./pages/EditProgramPage"));
const AdminPortalPreview = lazy(() => import("./pages/AdminPortalPreview"));
const AdminPortalLanding = lazy(() => import("./pages/AdminPortalLanding"));
const AdminPortalRouteNotFound = lazy(() => import("./pages/AdminPortalRouteNotFound"));

import "@/App.css";
import { createSupportClient } from "./support/supportSessionClient";
import { SupportSessionProvider } from "./support/SupportSessionProvider";
import { SupportSessionBanner } from "./support/SupportSessionControls";
import OrganizationWorkspace from "./support/OrganizationWorkspace";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
  || (window.location.hostname.endsWith("sporthouse.ai") ? "https://api.sporthouse.ai" : "");
export const API = `${BACKEND_URL}/api`;
export const COOKIE_AUTH_MARKER = "cookie-session";

// The durable credential is an HttpOnly cookie on the API origin.
axios.defaults.withCredentials = true;

// Create axios instance with interceptors
const axiosInstance = axios.create({
  baseURL: API,
  withCredentials: true
});

// Add response interceptor to handle auth errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only clear auth if it's a 401 and not during login/register
    if (error.response?.status === 401 && 
        !error.config?.url?.includes('/auth/login') && 
        !error.config?.url?.includes('/auth/register')) {
      console.log("Auth error detected, will redirect to login");
      // Don't automatically clear - let the checkAuth handle it
    }
    return Promise.reject(error);
  }
);

// Export axios instance for use in components
export { axiosInstance };

// Auth Context
const AuthContext = createContext(null);

// Request-local authorization projection; the real owner remains the actor.
export function OrganizationAuthority({organization, children}) {
  const auth = useAuth();
  const user = {...auth.user, actor: auth.user, actor_role: auth.user.role,
    role: "org_admin", is_super_admin: false, org_id: organization.org_id,
    organization_workspace: true, admin_access_level: "full",
    admin_permissions: ["programs", "registrations", "payments", "teams", "checkin", "reports", "settings"]};
  return <AuthContext.Provider value={{...auth, user, switchRole: async()=>{throw new Error("Exit the organization workspace before switching roles");}}}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Compatibility marker for components that still construct Authorization headers.
  // It is not a credential; the API ignores it and authenticates the HttpOnly cookie.
  const [token, setToken] = useState(null);
  // Optional measurement admission only; never changes auth or account behavior.
  // loading=false/user=null also describes network errors, so it is NOT proof
  // of anonymous auth. Only a current /auth/me 401 may open the analytics gate.
  const measurementEpoch = useRef(0);
  const measurementMutations = useRef(0);
  const publishMeasurement = (anonymous) => {
    // Optional telemetry must never interrupt essential auth (including mixed
    // cached assets during a release). Missing bridge stays default-denied.
    try { window.SporthouseAnalytics?.setAnonymous?.(anonymous); } catch { /* no auth effect */ }
    try { window.SporthouseMeta?.setAnonymous?.(anonymous); } catch { /* no auth effect */ }
  };
  const denyMeasurement = () => {
    publishMeasurement(false);
    return ++measurementEpoch.current;
  };

  const beginMeasurementMutation = () => {
    measurementMutations.current += 1;
    denyMeasurement();
  };
  const endMeasurementMutation = () => {
    measurementMutations.current -= 1;
    denyMeasurement();
  };

  const checkAuth = async () => {
    const epoch = denyMeasurement();
    // Exchange short-lived OAuth handoff codes. No bearer token enters the URL.
    const urlParams = new URLSearchParams(window.location.search);
    const handoffCode = urlParams.get('auth_code');
    if (handoffCode) {
      beginMeasurementMutation();
      urlParams.delete('auth_code');
      const cleanQuery = urlParams.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${cleanQuery ? `?${cleanQuery}` : ''}`);
      try {
        await axios.post(`${API}/auth/handoff/exchange`, { code: handoffCode });
        const currentUser = await axios.get(`${API}/auth/me`);
        denyMeasurement();
        setUser(currentUser.data);
        setToken(COOKIE_AUTH_MARKER);
        setLoading(false);
        return;
      } catch (error) {
        setUser(null);
        setToken(null);
        setLoading(false);
        return;
      } finally {
        endMeasurementMutation();
      }
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      denyMeasurement();
      setUser(response.data);
      setToken(COOKIE_AUTH_MARKER);
    } catch (error) {
      // Only clear token on genuine auth errors (401), not network issues
      if (error.response?.status === 401) {
        if (epoch === measurementEpoch.current && measurementMutations.current === 0) publishMeasurement(true);
        console.log("Token expired or invalid, clearing auth");
        setToken(null);
        setUser(null);
      } else {
        // Network error or server issue - keep the token, user might still be valid
        console.log("Auth check failed due to network/server error, keeping token");
        // Preserve current in-memory state for transient network failures.
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
    
    // Re-check auth when tab becomes visible (user returns to app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAuth();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      denyMeasurement();
    };
  }, []);

  const login = async (email, password, contextOrgId = null) => {
    beginMeasurementMutation();
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        context_org_id: contextOrgId
      });
      const { user: fallbackUser } = response.data;
      const currentUser = await axios.get(`${API}/auth/me`);
      const userData = currentUser.data || fallbackUser;
      denyMeasurement();
      setToken(COOKIE_AUTH_MARKER);
      setUser(userData);
      return userData;
    } finally {
      endMeasurementMutation();
    }
  };

  const register = async (email, password, name, phone) => {
    beginMeasurementMutation();
    try {
      const response = await axios.post(`${API}/auth/register`, { email, password, name, phone });
      const { user: userData } = response.data;
      denyMeasurement();
      setToken(COOKIE_AUTH_MARKER);
      setUser(userData);
      return userData;
    } finally {
      endMeasurementMutation();
    }
  };

  const loginWithGoogle = (userData) => {
    denyMeasurement();
    setToken(COOKIE_AUTH_MARKER);
    setUser(userData);
  };

  const logout = async () => {
    beginMeasurementMutation();
    try {
      try {
        await axios.post(`${API}/auth/logout`, {});
      } catch (e) {
        console.error("Logout error", e);
      }
      localStorage.removeItem("userRoles");
      setToken(null);
      setUser(null);
    } finally {
      endMeasurementMutation();
    }
  };

  const switchRole = async (role, orgId = null) => {
    beginMeasurementMutation();
    try {
      try {
        const response = await axios.post(
          `${API}/auth/switch-role`,
          { role, org_id: orgId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const { user: fallbackUser } = response.data;
        const currentUser = await axios.get(`${API}/auth/me`);
        const userData = currentUser.data || fallbackUser;
        denyMeasurement();
        setToken(COOKIE_AUTH_MARKER);
        setUser(userData);
        return userData;
      } catch (error) {
        console.error("Role switch error:", error);
        throw error;
      }
    } finally {
      endMeasurementMutation();
    }
  };

  const value = useMemo(() => ({
    user, 
    token, 
    loading, 
    login, 
    register, 
    logout, 
    switchRole,
    loginWithGoogle, 
    checkAuth
  }), [user, token, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
export const requiredAdminPermission = (pathname) => {
  const path = String(pathname || "").toLowerCase();
  const canonical = path.match(/^\/org\/[^/]+\/(.*)$/)?.[1];
  if (canonical) {
    if (/^(finance\/reports|connect)(\/|$)/.test(canonical)) return "reports";
    if (/^finance(\/|$)/.test(canonical)) return "payments";
    if (/^programs(\/|$)/.test(canonical)) return "programs";
    if (/^people(\/|$)/.test(canonical)) return "registrations";
    if (/^(operations|communications|support|extensions\/(wstl|tournaments|leagues))(\/|$)/.test(canonical)) return "teams";
    if (/^(settings|marketplace|website|extensions\/developer)(\/|$)/.test(canonical)) return "settings";
  }
  if (/^\/admin\/(financials|invoices|promo-codes|payment-plans|org-invoices)/.test(path)) return "payments";
  if (path.startsWith("/admin/reports")) return "reports";
  if (path.startsWith("/admin/connect")) return "reports";
  if (path.startsWith("/admin/programs")) return "programs";
  if (/^\/admin\/(people|households|coaches|compliance)/.test(path)) return "registrations";
  if (/^\/admin\/(scheduling|facilities|messaging|social|support)/.test(path)) return "teams";
  if (/^\/admin\/(wstl|modules)/.test(path)) return "teams";
  if (/^\/admin\/(settings|marketplace|developer|import|website)/.test(path)) return "settings";
  return null;
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    const returnPath = adminPortalReturnPath(location);
    return <Navigate to={`/login?redirect=${encodeURIComponent(returnPath)}`} replace />;
  }

  if (!isAdminPortalHost() && user.role === "super_admin" && location.pathname.startsWith("/admin")) {
    // Registration requires a real org_admin session on the server. This staging
    // handoff explains that boundary; it never mounts the registration console.
    const developerBoundary = isStagingSharedPortal() && !isAdminPortalEnabled()
      && location.pathname === "/admin/developer";
    if (developerBoundary) return <Navigate to="/platform/developer" replace />;
    return <PlatformAdminRedirect destination={legacyPlatformAdminDestination(location.pathname) || "/platform/not-found"} />;
  }

  // Check role access - also allow coach routes if user has coach_id
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Special case: allow access to coach routes if user has coach_id
    if (allowedRoles.includes("coach") && user.coach_id) {
      return children;
    }
    if (isAdminPortalHost()) {
      return (
        <main className="grid min-h-screen place-items-center bg-[#f5f7f4] p-6">
          <section className="w-full max-w-lg rounded-2xl border border-black/10 bg-white p-8" role="alert">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#168c78]">Sporthouse platform</p>
            <h1 className="mt-3 text-2xl font-semibold">Platform access required</h1>
            <p className="mt-3 text-sm leading-6 text-black/60">Organization administration remains available in the Sporthouse app.</p>
            <a className="mt-6 inline-flex rounded-xl bg-[#101a19] px-5 py-3 text-sm font-semibold text-white" href="https://app.sporthouse.ai/admin">Open organization administration</a>
          </section>
        </main>
      );
    }
    return <Navigate to="/dashboard" replace />;
  }

  const requiredPermission = user.role === "org_admin" ? requiredAdminPermission(location.pathname) : null;
  if (requiredPermission && Array.isArray(user.admin_permissions) && !user.admin_permissions.includes(requiredPermission)) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#f5f7f4] p-6">
        <section className="w-full max-w-lg rounded-3xl border border-[#d9e0da] bg-white p-8 text-center shadow-sm" role="alert">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#087f67]">Access restricted</p>
          <h1 className="mt-2 text-2xl font-semibold text-[#101a19]">Your administrator access does not include this area.</h1>
          <p className="mt-3 text-sm text-[#60706a]">Ask a full administrator to update your access level in Organization Settings.</p>
          <a href="/admin" className="mt-6 inline-flex rounded-xl bg-[#101a19] px-5 py-3 text-sm font-semibold text-white">Return to Today</a>
        </section>
      </main>
    );
  }

  return children;
};

const PlatformAdminRedirect = ({ destination }) => {
  const location = useLocation();
  const safeDestination = destination
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  useEffect(() => {
    window.location.replace(getAdminPortalUrl(`${safeDestination}${location.search || ""}${location.hash || ""}`));
  }, [location.hash, location.search, safeDestination]);
  return <PageLoader />;
};

// Program Redirect Component - Redirects /register/:programId to /org/:subdomain/program/:programId
const ProgramRedirect = () => {
  const { programId, slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAndRedirect = async () => {
      try {
        const identifier = programId || slug;
        const isSlugRoute = location.pathname.startsWith('/r/');
        
        const endpoint = isSlugRoute 
          ? `${API}/programs/by-slug/${identifier}`
          : `${API}/programs/${identifier}`;
        
        const programRes = await axios.get(endpoint);
        const orgRes = await axios.get(`${API}/organizations/${programRes.data.org_id}`);
        
        // Redirect to the new org portal program page
        const subdomain = orgRes.data.subdomain || orgRes.data.org_id;
        navigate(`/org/${subdomain}/program/${programRes.data.program_id}`, { replace: true });
      } catch (error) {
        console.error("Error fetching program:", error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAndRedirect();
  }, [programId, slug, navigate, location.pathname]);

  if (loading) {
    return <PageLoader />;
  }

  return null;
};

const TournamentLiveRedirect = () => {
  const { tournamentId } = useParams();
  return <Navigate to={`/tournaments/${tournamentId}/live`} replace />;
};

// Program Redirect for Subdomain - Redirects /register/:programId to /program/:programId
const ProgramRedirectSubdomain = ({ subdomain }) => {
  const { programId, slug } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAndRedirect = async () => {
      try {
        if (programId) {
          navigate(`/register/${programId}`, { replace: true });
          return;
        }

        const { data: program } = await axios.get(`${API}/programs/by-slug/${encodeURIComponent(slug)}`);
        const { data: organization } = await axios.get(`${API}/organizations/${program.org_id}`);
        if (organization.subdomain !== subdomain) {
          throw new Error("Program does not belong to this organization");
        }
        navigate(`/register/${program.program_id}`, { replace: true });
      } catch (error) {
        console.error("Error resolving program registration link:", error);
        navigate('/', { replace: true });
      }
    };

    fetchAndRedirect();
  }, [programId, slug, subdomain, navigate]);

  return <PageLoader />;
};

// Wrapper components for subdomain mode (pass subdomain as prop instead of URL param)
const OrgPortalWithSubdomain = ({ subdomain }) => {
  return (
    <Suspense fallback={<PageLoader />}>
      <OrgPortal subdomainProp={subdomain} />
    </Suspense>
  );
};

const OrgLoginWithSubdomain = ({ subdomain }) => {
  return (
    <Suspense fallback={<PageLoader />}>
      <OrgLogin subdomainProp={subdomain} />
    </Suspense>
  );
};

const OrgRegisterWithSubdomain = ({ subdomain }) => {
  return (
    <Suspense fallback={<PageLoader />}>
      <OrgRegister subdomainProp={subdomain} />
    </Suspense>
  );
};

const ShopWithSubdomain = ({ subdomain }) => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Shop subdomainProp={subdomain} />
    </Suspense>
  );
};

// Lazy Route wrapper for cleaner JSX
const LazyRoute = ({ element: Element, ...props }) => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Element {...props} />
    </Suspense>
  </ErrorBoundary>
);

// Protected Lazy Route wrapper
const ProtectedLazyRoute = ({ element: Element, allowedRoles, ...props }) => (
  <ProtectedRoute allowedRoles={allowedRoles}>
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Element {...props} />
      </Suspense>
    </ErrorBoundary>
  </ProtectedRoute>
);

function SupportBoundary({ children }) {
  const {user,token}=useAuth();
  const client=useMemo(()=>createSupportClient(API, undefined, token),[token]);
  return <SupportSessionProvider actor={user} client={client}><SupportSessionBanner />{children}</SupportSessionProvider>;
}

// App Router
function AppRouter() {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  
  // Detect if we're on an organization subdomain
  const subdomain = getSubdomain();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id (Emergent Google OAuth)
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<PageLoader />}>
        <AuthCallback />
      </Suspense>
    );
  }

  // The production app owns MCP consent/cookies. Expose only this owner
  // workspace here, not the unrelated platform portal or org-admin console.
  if (window.location.origin === 'https://app.sporthouse.ai'
      && location.pathname === '/platform/developer') {
    return <ProtectedRoute allowedRoles={["super_admin"]}><PlatformMcpRegistration /></ProtectedRoute>;
  }

  if (isAdminPortalEnabled() || (isStagingSharedPortal() && (location.pathname.startsWith('/platform/') || location.pathname.startsWith('/workspace/organizations/')))) {
    return (
      <Routes>
        <Route path="/" element={<ProtectedLazyRoute element={AdminPortalLanding} allowedRoles={["super_admin"]} />} />
        <Route path="/login" element={<LazyRoute element={LoginPage} />} />
        <Route path="/auth/callback" element={<LazyRoute element={AuthCallback} />} />
        <Route path="/auth/google/callback" element={<LazyRoute element={GoogleCallback} />} />
        {isStagingSharedPortal() && !isAdminPortalEnabled() && <Route path="/platform/developer" element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <PlatformMcpRegistration />
          </ProtectedRoute>
        } />}
        <Route path="/platform/organizations/:orgId" element={<ProtectedLazyRoute element={AdminPortalPreview} mode="platform" allowedRoles={["super_admin"]} />} />
        <Route path="/workspace/organizations/:workspaceOrgId/*" element={<ProtectedLazyRoute element={OrganizationWorkspace} allowedRoles={["super_admin"]} />} />
        <Route path="/org/:orgSlug/*" element={<ProtectedLazyRoute element={OrganizationWorkspace} allowedRoles={["super_admin"]} />} />
        <Route path="/platform/*" element={<ProtectedLazyRoute element={AdminPortalPreview} mode="platform" allowedRoles={["super_admin"]} />} />
        <Route path="/profile" element={<ProtectedLazyRoute element={AdminProfile} allowedRoles={["super_admin"]} />} />
        <Route path="*" element={<ProtectedLazyRoute element={AdminPortalRouteNotFound} allowedRoles={["super_admin"]} />} />
      </Routes>
    );
  }

  // Keep host isolation above this gate; org aliases must not mount legacy pages.
  if (!authLoading && user?.role === "org_admin" && isLegacyOrganizationAdminPath(location.pathname)) {
    return <ProtectedRoute allowedRoles={["org_admin"]}><OrganizationAdminRedirect /></ProtectedRoute>;
  }

  const isPublicRegistrationPath = location.pathname.startsWith("/r/")
    || location.pathname.startsWith("/register/")
    || location.pathname.startsWith("/registration/confirmation/")
    || location.pathname.startsWith("/program/")
    || location.pathname.startsWith("/tournaments/");

  if (isWebsiteProHost() && !isWebsiteProAdminPath(location.pathname) && !isPublicRegistrationPath) {
    return (
      <Routes>
        <Route
          path="*"
          element={(
            <LazyRoute
              element={PublicWebsitePro}
              unavailableFallback={
                subdomain
                  ? <OrgPortalWithSubdomain subdomain={subdomain} />
                  : null
              }
            />
          )}
        />
      </Routes>
    );
  }

  if (subdomain && !authLoading && user?.role === "super_admin") {
    window.location.replace(`${getMainAppUrl()}/admin`);
    return <PageLoader />;
  }

  // If we're on an org subdomain (e.g., springfield.sporthouse.ai),
  // show org-specific routes
  if (subdomain) {
    return (
      <Routes>
        {/* Org Portal Routes - subdomain mode */}
        <Route path="/" element={<OrgPortalWithSubdomain subdomain={subdomain} />} />
        <Route path="/program/:programId" element={<OrgPortalWithSubdomain subdomain={subdomain} />} />
        <Route path="/shop" element={<ShopWithSubdomain subdomain={subdomain} />} />
        <Route path="/login" element={<OrgLoginWithSubdomain subdomain={subdomain} />} />
        <Route path="/register" element={<OrgRegisterWithSubdomain subdomain={subdomain} />} />
        <Route path="/forgot-password" element={<LazyRoute element={ForgotPassword} />} />
        <Route path="/reset-password" element={<LazyRoute element={ResetPassword} />} />
        <Route path="/verify-email" element={<LazyRoute element={VerifyEmail} />} />
        
        {/* Auth callbacks still need to work */}
        <Route path="/auth/callback" element={<LazyRoute element={AuthCallback} />} />
        <Route path="/auth/google/callback" element={<LazyRoute element={GoogleCallback} />} />
        
        {/* Durable routed registration */}
        <Route path="/register/:programId" element={<LazyRoute element={RegistrationPage} />} />
        <Route path="/r/:slug" element={<ProgramRedirectSubdomain subdomain={subdomain} />} />
        <Route path="/tournaments/:tournamentId/apply" element={<LazyRoute element={PublicTournamentApplication} />} />
        <Route path="/tournaments/:tournamentId/live" element={<LazyRoute element={PublicTournamentLive} />} />
        <Route path="/tournaments/:tournamentId" element={<TournamentLiveRedirect />} />
        
        {/* Public Score Submission */}
        <Route path="/score/:gameCode" element={<LazyRoute element={PublicScoreSubmit} />} />
        
        {/* Retired coach-profile URLs return visitors to the organization portal. */}
        <Route path="/c/:slug" element={<Navigate to="/" replace />} />
        <Route path="/coaches" element={<Navigate to="/" replace />} />
        <Route path="/discover" element={<LazyRoute element={DiscoverPrograms} />} />
        
        {/* Protected routes for logged-in users on subdomain */}
        <Route path="/onboarding" element={<ProtectedLazyRoute element={Onboarding} />} />
        <Route path="/dashboard" element={<ProtectedLazyRoute element={Dashboard} />} />
        <Route path="/programs" element={<ProtectedLazyRoute element={Programs} />} />
        <Route path="/programs/:programId" element={<ProtectedLazyRoute element={ProgramDetail} />} />
        <Route path="/household" element={<ProtectedLazyRoute element={Household} />} />
        <Route path="/household/new" element={<ProtectedLazyRoute element={FamilyFormPage} resource="household" />} />
        <Route path="/household/:householdId/edit" element={<ProtectedLazyRoute element={FamilyFormPage} resource="household" />} />
        <Route path="/household/:householdId/athletes/new" element={<ProtectedLazyRoute element={FamilyFormPage} resource="athlete" />} />
        <Route path="/household/:householdId/athletes/:playerId/edit" element={<ProtectedLazyRoute element={FamilyFormPage} resource="athlete" />} />
        <Route path="/household/player/:playerId" element={<ProtectedLazyRoute element={PlayerProfile} />} />
        <Route path="/household/member/:memberId" element={<ProtectedLazyRoute element={MemberProfile} />} />
        <Route path="/registrations" element={<ProtectedLazyRoute element={Registrations} />} />
        <Route path="/registration/confirmation/:registrationId" element={<ProtectedLazyRoute element={RegistrationConfirmation} />} />
        <Route path="/tryout-invites" element={<ProtectedLazyRoute element={TryoutInvites} />} />
        <Route path="/payment-success" element={<ProtectedLazyRoute element={PaymentSuccess} />} />
        <Route path="/payment-methods" element={<ProtectedLazyRoute element={PaymentMethods} />} />
        <Route path="/profile" element={<ProtectedLazyRoute element={Profile} />} />
        
        {/* Admin routes on subdomain */}
        <Route path="/admin" element={<ProtectedLazyRoute element={AdminDashboard} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/programs" element={<ProtectedLazyRoute element={AdminPrograms} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/programs/new" element={<ProtectedLazyRoute element={ProgramTypeSelector} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/programs/new/:programType" element={<ProtectedLazyRoute element={CreateProgramPage} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/programs/:id/edit" element={<ProtectedLazyRoute element={EditProgramPage} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/programs/:programId" element={<ProtectedLazyRoute element={AdminProgramDashboard} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/households" element={<ProtectedLazyRoute element={AdminHouseholds} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/households/:householdId" element={<ProtectedLazyRoute element={AdminHouseholdDetail} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/financials" element={<ProtectedLazyRoute element={AdminFinancials} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/invoices" element={<ProtectedLazyRoute element={AdminInvoices} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/registrations" element={<Navigate to="/admin/people?view=enrollments" replace />} />
        <Route path="/admin/social" element={<ProtectedLazyRoute element={AdminSocial} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/messaging" element={<ProtectedLazyRoute element={AdminMessaging} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/support" element={<ProtectedLazyRoute element={AdminSupport} allowedRoles={["super_admin", "org_admin"]} />} />
        {/* Redirect old meetings route to scheduling page */}
        <Route path="/admin/meetings" element={<Navigate to="/admin/scheduling?tab=meetings" replace />} />
        <Route path="/admin/scheduling" element={<ProtectedLazyRoute element={AdminScheduling} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/import" element={<ProtectedLazyRoute element={AdminImport} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/staff" element={<Navigate to="/admin/people" replace />} />
        <Route path="/admin/people" element={<ProtectedLazyRoute element={AdminPeople} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/facilities" element={<ProtectedLazyRoute element={AdminFacilities} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/coaches" element={<ProtectedLazyRoute element={AdminCoaches} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/coaches/:coachId" element={<Navigate to="/admin/people?type=coach" replace />} />
        <Route path="/admin/compliance" element={<Navigate to="/admin/people?status=issues" replace />} />
        <Route path="/admin/promo-codes" element={<ProtectedLazyRoute element={AdminPromoCodes} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/payment-plans" element={<ProtectedLazyRoute element={AdminPaymentPlans} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/org-invoices" element={<Navigate to="/admin/financials?view=receivables" replace />} />
        <Route path="/admin/reports/*" element={<ProtectedLazyRoute element={AdminReports} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/settings" element={<ProtectedLazyRoute element={AdminOrganizationSettings} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/marketplace" element={<ProtectedLazyRoute element={AdminMarketplace} allowedRoles={["org_admin"]} />} />
        <Route path="/admin/website" element={<ProtectedLazyRoute element={AdminWebsitePro} allowedRoles={["org_admin"]} />} />
        <Route path="/admin/wstl" element={<ProtectedLazyRoute element={AdminWstlControlCenter} allowedRoles={["org_admin"]} />} />
        {process.env.REACT_APP_SPORTHOUSE_MCP_ENABLED === "true" && <Route path="/mcp/connect" element={<ProtectedLazyRoute element={MCPConnect} allowedRoles={["org_admin", "super_admin"]} />} />}
        <Route path="/admin/developer" element={<ProtectedLazyRoute element={AdminDeveloperPlatform} allowedRoles={["org_admin"]} />} />
        <Route path="/admin/connect/*" element={<ProtectedLazyRoute element={AdminConnect} allowedRoles={["org_admin"]} />} />
        <Route path="/oauth/authorize" element={<ProtectedLazyRoute element={DeveloperOAuthConsent} allowedRoles={window.location.search === "?mcp=1" ? ["org_admin", "super_admin"] : ["org_admin"]} />} />
        <Route path="/admin/modules/tournaments" element={<ProtectedLazyRoute element={AdminTournamentModule} allowedRoles={["org_admin"]} />} />
        <Route path="/admin/modules/leagues" element={<ProtectedLazyRoute element={AdminLeagueModule} allowedRoles={["org_admin"]} />} />
        <Route path="/admin/settings/import" element={<ProtectedLazyRoute element={ImportHub} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/profile" element={<ProtectedLazyRoute element={AdminProfile} allowedRoles={["super_admin", "org_admin"]} />} />
        <Route path="/admin/platform" element={<PlatformAdminRedirect destination="/platform/settings" />} />
        <Route path="/admin/platform/*" element={<PlatformAdminRedirect destination={`/platform/settings/${location.pathname.slice("/admin/platform/".length)}`} />} />
        <Route path="/admin/marketing/demo-funnel" element={<PlatformAdminRedirect destination="/platform/marketing/demo-funnel" />} />
        <Route path="/admin/donations" element={<PlatformAdminRedirect destination="/platform/donations" />} />
        <Route path="/admin/team" element={<Navigate to="/admin/people" replace />} />
        
        {/* Coach routes on subdomain */}
        
        <Route path="/coach/register" element={<LazyRoute element={CoachRegistration} />} />
        <Route path="/coach/signup" element={<LazyRoute element={IndependentCoachSignup} />} />
        <Route path="/coach/become" element={<ProtectedLazyRoute element={BecomeCoach} />} />
        <Route path="/coach/pending" element={<LazyRoute element={CoachPending} />} />
        <Route path="/coach/dashboard" element={<ProtectedLazyRoute element={CoachDashboard} allowedRoles={["coach"]} />} />
        <Route path="/coach/profile" element={<Navigate to="/coach/dashboard" replace />} />
        <Route path="/coach/compliance" element={<ProtectedLazyRoute element={CoachCompliance} allowedRoles={["coach"]} />} />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Main app routes (app.sporthouse.ai or localhost)
  return (
    <Routes>
      <Route path="/" element={<LazyRoute element={LoginPage} />} />
      <Route path="/login" element={<LazyRoute element={LoginPage} />} />
      <Route path="/register" element={<LazyRoute element={RegisterPage} />} />
      <Route path="/forgot-password" element={<LazyRoute element={ForgotPassword} />} />
      <Route path="/reset-password" element={<LazyRoute element={ResetPassword} />} />
      <Route path="/verify-email" element={<LazyRoute element={VerifyEmail} />} />
      <Route path="/auth/callback" element={<LazyRoute element={AuthCallback} />} />
      <Route path="/auth/google/callback" element={<LazyRoute element={GoogleCallback} />} />
      
      {/* Durable routed registration */}
      <Route path="/register/:programId" element={<LazyRoute element={RegistrationPage} />} />
      <Route path="/r/:slug" element={<ProgramRedirect />} />
      
      {/* Public Score Submission Page - No auth required */}
      <Route path="/score/:gameCode" element={<LazyRoute element={PublicScoreSubmit} />} />
      
      {/* Retired coach-profile URLs return visitors to program discovery. */}
      <Route path="/coaches" element={<Navigate to="/discover" replace />} />
      <Route path="/c/:slug" element={<Navigate to="/discover" replace />} />

      {/* Program Discovery - No auth required */}
      <Route path="/discover" element={<LazyRoute element={DiscoverPrograms} />} />
      <Route path="/tournaments/:tournamentId/apply" element={<LazyRoute element={PublicTournamentApplication} />} />
      <Route path="/tournaments/:tournamentId/live" element={<LazyRoute element={PublicTournamentLive} />} />
      <Route path="/tournaments/:tournamentId" element={<TournamentLiveRedirect />} />
      
      {/* Public Organization Portal (path-based fallback for development) */}
      <Route path="/org/:subdomain" element={<LazyRoute element={OrgPortal} />} />
      <Route path="/org/:subdomain/program/:programId" element={<LazyRoute element={OrgPortal} />} />
      <Route path="/leagues/:programId/schedule" element={<LazyRoute element={PublicLeagueSchedule} />} />
      <Route path="/org/:subdomain/shop" element={<LazyRoute element={Shop} />} />
      <Route path="/org/:subdomain/login" element={<LazyRoute element={OrgLogin} />} />
      <Route path="/org/:subdomain/register" element={<LazyRoute element={OrgRegister} />} />

      {/* Authenticated organization administration remains on the app host. */}
      <Route path="/org/:orgSlug/*" element={<ProtectedLazyRoute element={AdminPortalPreview} mode="organization" allowedRoles={["org_admin"]} />} />

      {/* Coach Routes */}
      <Route path="/coach/register" element={<LazyRoute element={CoachRegistration} />} />
      <Route path="/coach/signup" element={<LazyRoute element={IndependentCoachSignup} />} />
      <Route path="/coach/become" element={<ProtectedLazyRoute element={BecomeCoach} />} />
      <Route path="/coach/pending" element={<LazyRoute element={CoachPending} />} />
      <Route path="/coach/dashboard" element={<ProtectedLazyRoute element={CoachDashboard} allowedRoles={["coach"]} />} />
      <Route path="/coach/profile" element={<Navigate to="/coach/dashboard" replace />} />
      
      {/* Onboarding */}
      <Route path="/onboarding" element={<ProtectedLazyRoute element={Onboarding} />} />
      
      {/* Parent Routes */}
      <Route path="/dashboard" element={<ProtectedLazyRoute element={Dashboard} />} />
      <Route path="/programs" element={<ProtectedLazyRoute element={Programs} />} />
      <Route path="/programs/:programId" element={<ProtectedLazyRoute element={ProgramDetail} />} />
      <Route path="/household" element={<ProtectedLazyRoute element={Household} />} />
      <Route path="/household/new" element={<ProtectedLazyRoute element={FamilyFormPage} resource="household" />} />
      <Route path="/household/:householdId/edit" element={<ProtectedLazyRoute element={FamilyFormPage} resource="household" />} />
      <Route path="/household/:householdId/athletes/new" element={<ProtectedLazyRoute element={FamilyFormPage} resource="athlete" />} />
      <Route path="/household/:householdId/athletes/:playerId/edit" element={<ProtectedLazyRoute element={FamilyFormPage} resource="athlete" />} />
      <Route path="/household/player/:playerId" element={<ProtectedLazyRoute element={PlayerProfile} />} />
      <Route path="/household/member/:memberId" element={<ProtectedLazyRoute element={MemberProfile} />} />
      <Route path="/registrations" element={<ProtectedLazyRoute element={Registrations} />} />
      <Route path="/registration/confirmation/:registrationId" element={<ProtectedLazyRoute element={RegistrationConfirmation} />} />
      <Route path="/tryout-invites" element={<ProtectedLazyRoute element={TryoutInvites} />} />
      <Route path="/payment-success" element={<ProtectedLazyRoute element={PaymentSuccess} />} />
      <Route path="/payment-methods" element={<ProtectedLazyRoute element={PaymentMethods} />} />
      <Route path="/profile" element={<ProtectedLazyRoute element={Profile} />} />
      <Route path="/create-organization" element={<ProtectedLazyRoute element={CreateOrganization} />} />
      <Route path="/organization-application/:orgId" element={<ProtectedLazyRoute element={OrganizationApplicationStatus} />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedLazyRoute element={AdminDashboard} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/programs" element={<ProtectedLazyRoute element={AdminPrograms} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/programs/new" element={<ProtectedLazyRoute element={ProgramTypeSelector} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/programs/new/:programType" element={<ProtectedLazyRoute element={CreateProgramPage} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/programs/:id/edit" element={<ProtectedLazyRoute element={EditProgramPage} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/programs/:programId" element={<ProtectedLazyRoute element={AdminProgramDashboard} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/households" element={<ProtectedLazyRoute element={AdminHouseholds} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/households/:householdId" element={<ProtectedLazyRoute element={AdminHouseholdDetail} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/financials" element={<ProtectedLazyRoute element={AdminFinancials} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/invoices" element={<ProtectedLazyRoute element={AdminInvoices} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/registrations" element={<Navigate to="/admin/people?view=enrollments" replace />} />
      <Route path="/admin/social" element={<ProtectedLazyRoute element={AdminSocial} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/messaging" element={<ProtectedLazyRoute element={AdminMessaging} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/support" element={<ProtectedLazyRoute element={AdminSupport} allowedRoles={["super_admin", "org_admin"]} />} />
      {/* Redirect old meetings route to scheduling page */}
      <Route path="/admin/meetings" element={<Navigate to="/admin/scheduling?tab=meetings" replace />} />
      <Route path="/admin/scheduling" element={<ProtectedLazyRoute element={AdminScheduling} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/import" element={<ProtectedLazyRoute element={AdminImport} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/staff" element={<Navigate to="/admin/people" replace />} />
      <Route path="/admin/people" element={<ProtectedLazyRoute element={AdminPeople} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/team" element={<Navigate to="/admin/people" replace />} />
      <Route path="/admin/facilities" element={<ProtectedLazyRoute element={AdminFacilities} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/coaches" element={<ProtectedLazyRoute element={AdminCoaches} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/coaches/:coachId" element={<Navigate to="/admin/people?type=coach" replace />} />
      <Route path="/admin/compliance" element={<Navigate to="/admin/people?status=issues" replace />} />
      <Route path="/admin/organizations" element={<PlatformAdminRedirect destination="/platform/organizations" />} />
      <Route path="/admin/organizations/:orgId" element={<PlatformAdminRedirect destination={`/platform/organizations/${location.pathname.slice("/admin/organizations/".length)}`} />} />
      <Route path="/admin/users" element={<PlatformAdminRedirect destination="/platform/users" />} />
      <Route path="/admin/settings" element={<ProtectedLazyRoute element={AdminOrganizationSettings} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/marketplace" element={<ProtectedLazyRoute element={AdminMarketplace} allowedRoles={["org_admin"]} />} />
      <Route path="/admin/website" element={<ProtectedLazyRoute element={AdminWebsitePro} allowedRoles={["org_admin"]} />} />
      <Route path="/admin/wstl" element={<ProtectedLazyRoute element={AdminWstlControlCenter} allowedRoles={["org_admin"]} />} />
      {process.env.REACT_APP_SPORTHOUSE_MCP_ENABLED === "true" && <Route path="/mcp/connect" element={<ProtectedLazyRoute element={MCPConnect} allowedRoles={["org_admin", "super_admin"]} />} />}
        <Route path="/admin/developer" element={<ProtectedLazyRoute element={AdminDeveloperPlatform} allowedRoles={["org_admin"]} />} />
      <Route path="/admin/connect/*" element={<ProtectedLazyRoute element={AdminConnect} allowedRoles={["org_admin"]} />} />
      <Route path="/oauth/authorize" element={<ProtectedLazyRoute element={DeveloperOAuthConsent} allowedRoles={window.location.search === "?mcp=1" ? ["org_admin", "super_admin"] : ["org_admin"]} />} />
      <Route path="/admin/modules/tournaments" element={<ProtectedLazyRoute element={AdminTournamentModule} allowedRoles={["org_admin"]} />} />
      <Route path="/admin/modules/leagues" element={<ProtectedLazyRoute element={AdminLeagueModule} allowedRoles={["org_admin"]} />} />
      <Route path="/admin/settings/import" element={<ProtectedLazyRoute element={ImportHub} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/promo-codes" element={<ProtectedLazyRoute element={AdminPromoCodes} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/profile" element={<ProtectedLazyRoute element={AdminProfile} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/reports/*" element={<ProtectedLazyRoute element={AdminReports} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/payment-plans" element={<ProtectedLazyRoute element={AdminPaymentPlans} allowedRoles={["super_admin", "org_admin"]} />} />
      <Route path="/admin/org-invoices" element={<Navigate to="/admin/financials?view=receivables" replace />} />
      <Route path="/admin/platform" element={<PlatformAdminRedirect destination="/platform/settings" />} />
      <Route path="/admin/platform/*" element={<PlatformAdminRedirect destination={`/platform/settings/${location.pathname.slice("/admin/platform/".length)}`} />} />
      <Route path="/admin/marketing/demo-funnel" element={<PlatformAdminRedirect destination="/platform/marketing/demo-funnel" />} />
      <Route path="/admin/donations" element={<PlatformAdminRedirect destination="/platform/donations" />} />
      
      
      {/* Coach routes */}
      <Route path="/coach/compliance" element={<ProtectedLazyRoute element={CoachCompliance} allowedRoles={["coach"]} />} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <SupportBoundary><AppRouter /></SupportBoundary>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
